'use client';

import React, { useState } from 'react';
import HeroSection from './HeroSection';
import ContactModal from './ContactModal';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import EndorsementFormModal from './EndorsementFormModal';

interface Endorsement {
  skill: string;
  endorsers: {
    name: string;
    title: string;
    image: string;
    alt: string;
  }[];
  count: number;
}

const HomepageInteractive: React.FC = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  const [isEndorsementFormOpen, setIsEndorsementFormOpen] = useState(false);

  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsContactModalOpen(false);
  };

  const toggleSkill = (skill: string) => {
    setExpandedSkill(expandedSkill === skill ? null : skill);
  };

  const endorsements: Endorsement[] = [
    {
      skill: "Laboratory Operations Management",
      count: 24,
      endorsers: [
        {
          name: "Dr. Sarah Mitchell",
          title: "Director of Quality Assurance, PharmaTech Solutions",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_18811c304-1763296452128.png",
          alt: "Professional woman with blonde hair in business attire smiling at camera"
        },
        {
          name: "James Rodriguez",
          title: "VP of Laboratory Services, EnviroTest Corp",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_104e32cd5-1763293338887.png",
          alt: "Hispanic man with beard wearing navy suit and tie in office setting"
        },
        {
          name: "Dr. Emily Chen",
          title: "Chief Scientific Officer, BioAnalytics Inc",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_17064d52c-1766741247223.png",
          alt: "Asian woman with glasses in white lab coat holding clipboard"
        }
      ]
    },
    {
      skill: "Regulatory Compliance & Quality Assurance",
      count: 31,
      endorsers: [
        {
          name: "Michael Thompson",
          title: "Compliance Manager, Global Testing Services",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f23d9c3d-1763295425663.png",
          alt: "African American man in gray suit with confident expression"
        },
        {
          name: "Dr. Lisa Anderson",
          title: "Quality Director, Precision Laboratories",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_128683b30-1763296674609.png",
          alt: "Professional woman with brown hair in business casual attire"
        }
      ]
    },
    {
      skill: "Team Leadership & Development",
      count: 19,
      endorsers: [
        {
          name: "Robert Martinez",
          title: "HR Director, LabCorp Regional",
          image: "https://images.unsplash.com/photo-1695830209166-161b6297541d",
          alt: "Man with short dark hair in blue button-down shirt smiling"
        },
        {
          name: "Jennifer Park",
          title: "Operations Manager, Analytical Services Group",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_10deca2fb-1763299994309.png",
          alt: "Asian woman with long black hair in professional blazer"
        }
      ]
    }
  ];

  return (
    <>
      <HeroSection onContactClick={handleContactClick} />
      
      {/* Peer Endorsements Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-primary via-primary/95 to-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl border border-border p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-accent/10 text-accent p-3 rounded-xl">
                  <Icon name="HandThumbUpIcon" size={28} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-2xl text-text-primary">
                    Peer Endorsements
                  </h3>
                  <p className="text-text-secondary text-sm">
                    Professional validations from industry colleagues
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsEndorsementFormOpen(true)}
                className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors duration-200 font-semibold flex items-center space-x-2"
              >
                <Icon name="PlusIcon" size={20} />
                <span>Add Endorsement</span>
              </button>
            </div>

            <div className="space-y-4">
              {endorsements.map((endorsement, index) => (
                <div
                  key={index}
                  className="border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-colors duration-200"
                >
                  <button
                    onClick={() => toggleSkill(endorsement.skill)}
                    className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="CheckBadgeIcon" size={20} className="text-accent" />
                      <span className="font-semibold text-text-primary">
                        {endorsement.skill}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-text-secondary">
                        {endorsement.count} endorsements
                      </span>
                      <Icon
                        name="ChevronDownIcon"
                        size={20}
                        className={`text-text-secondary transform transition-transform duration-200 ${
                          expandedSkill === endorsement.skill ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {expandedSkill === endorsement.skill && (
                    <div className="p-4 bg-card space-y-3">
                      {endorsement.endorsers.map((endorser, idx) => (
                        <div
                          key={idx}
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200"
                        >
                          <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            <AppImage
                              src={endorser.image}
                              alt={endorser.alt}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-text-primary truncate">
                              {endorser.name}
                            </div>
                            <div className="text-sm text-text-secondary truncate">
                              {endorser.title}
                            </div>
                          </div>
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ContactModal isOpen={isContactModalOpen} onClose={handleCloseModal} />
      <EndorsementFormModal 
        isOpen={isEndorsementFormOpen} 
        onClose={() => setIsEndorsementFormOpen(false)} 
      />
    </>
  );
};

export default HomepageInteractive;